import { faXmark } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button } from 'antd'
import React, { useMemo } from 'react'
import { antdFormError } from '../../constants/constants'
import { useStore } from '../../store/store'

const DeleteButton = ({
  isFromHeader,
  controlRowsClicked,
  setControlRowsClicked,
  boxId,
  parentKey,
  parentRowId,
  parentRowType,
  ...ButtonProps
}) => {
  const updateBox = useStore(({ updateBox }) => updateBox)
  const hideTableColumnsFromCanvas = useStore(({ hideTableColumnsFromCanvas }) => hideTableColumnsFromCanvas)
  const isHovered = controlRowsClicked.some(row => row.parentRowId === parentRowId && row.parentRowType === parentRowType && row.isToDelete)

  const basePosition = useMemo(() => {
    return isFromHeader ? "left" : "top"
  }, [isFromHeader])

  const oppositeBasePosition = useMemo(() => {
    return isFromHeader ? "right" : "bottom"
  }, [isFromHeader])

  const secondBasePosition = useMemo(() => {
    return isFromHeader ? "top" : "left"
  }, [isFromHeader])

  const fillValue = useMemo(() => {
    return isFromHeader ? "width" : "height"
  }, [isFromHeader])

  const handleOnMouseEnter = () => {
    setControlRowsClicked((prev) =>
      prev.map(row =>
        ({ ...row, isToDelete: true })
      )
    )
  }

  const handleOnMouseLeave = () => {
    setControlRowsClicked((prev) =>
      prev.map(row =>
        ({ ...row, isToDelete: false })
      )
    )
  }

  const handleOnClick = () => {
    const type = isFromHeader ? "columns" : "data"
    if (isFromHeader) {
      // Tableau variable : la suppression est mémorisée dans la config, sinon la
      // prochaine relecture de la table ramène la colonne. Une colonne au moins
      // reste, comme dans la suppression locale ci-dessous.
      const { boxes } = useStore.getState();
      const columns = boxes.find((b) => b.id === boxId)?.content?.columns ?? [];
      const keysToHide = controlRowsClicked
        .map(({ parentRowId }) => parentRowId)
        .filter((key) => columns.some((c) => c.key === key))
        .slice(0, Math.max(columns.length - 1, 0));
      hideTableColumnsFromCanvas(boxId, keysToHide);
    }
    updateBox(boxId, (box) => {
      controlRowsClicked.forEach(({ parentRowId }) => {
        const targetedElement = box.content[type].find((c) => parentRowId === c.key);
        if (targetedElement) {
          if (type === 'columns' && box.content.columns.length > 1) {
            const columnKey = targetedElement.key;
            box.content.data.forEach((row) => { delete row[columnKey]; delete row[`textAlign_${columnKey}`] });
            const removedColumnWidth = targetedElement.width * -1;
            if (box.content[type].length > 1) {
              const index = box.content[type].findIndex((c) => c.key === parentRowId);
              box.content[type].splice(index, 1);
            }
            box.content.columns.forEach((col) => {
              const toAdd = (-removedColumnWidth) / box.content.columns.length;
              const newColumnWidth = col.width + toAdd;
              col.width = newColumnWidth;
            });

            // Supprimer la colonne de columnsDisposition
            if (box.content.columnsDisposition && box.content.columnsDisposition.length > 0) {
              // Fonction récursive pour supprimer une colonne par sa key
              const removeColumnFromDisposition = (columns, keyToRemove) => {
                return columns
                  .map(column => {
                    if (column.key === keyToRemove) {
                      // Colonne à supprimer trouvée, retourner null pour la filtrer
                      return null;
                    }
                    if (column.children) {
                      // Colonne parente : supprimer récursivement dans les enfants
                      const filteredChildren = removeColumnFromDisposition(column.children, keyToRemove);
                      // Si après filtrage il ne reste plus d'enfants, supprimer le parent aussi
                      if (filteredChildren.length === 0) {
                        return null;
                      }
                      return {
                        ...column,
                        children: filteredChildren
                      };
                    }
                    // Colonne feuille qui n'est pas à supprimer
                    return column;
                  })
                  .filter(column => column !== null); // Filtrer les nulls
              };

              box.content.columnsDisposition = removeColumnFromDisposition(
                box.content.columnsDisposition,
                columnKey
              );
            }
          } else if (box.content[type].length > 1) {
            const index = box.content[type].findIndex((c) => c.key === parentRowId);

            // Si on supprime une ligne (type === 'data'), nettoyer les hiddenColumnsKeys de la ligne d'en dessous
            if (type === 'data' && index !== -1 && index + 1 < box.content.data.length) {
              if (index + 1 < box.content.data.length && box.content.data[index].hiddenColumnsKeys.length === 0) {
                const nextRow = box.content.data[index + 1];
                nextRow.hiddenColumnsKeys = [];
              }
            }

            box.content[type].splice(index, 1);
          }
        }
      });

    });
  }

  return (
    <div style={{
      position: "absolute",
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      [`${basePosition}`]: 0,
      [`${oppositeBasePosition}`]: 0,
      [`margin-${basePosition}`]: 'auto',
      [`margin-${oppositeBasePosition}`]: 'auto',
      [`${secondBasePosition}`]: -15,
      [`${fillValue}`]: "fit-content",
    }}>
      <Button
        onMouseEnter={handleOnMouseEnter}
        onMouseLeave={handleOnMouseLeave}
        onClick={handleOnClick}
        icon={<FontAwesomeIcon icon={faXmark}
          style={{
            transition: "color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1)",
            color: isHovered ? "white" : "rgba(0, 0, 0, 0.88)",
          }} />}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 20,
          height: 20,
          padding: 0,
          backgroundColor: isHovered ? antdFormError : undefined,
          borderColor: isHovered ? "white" : undefined,
          borderRadius: 20
        }}
        {...ButtonProps}
      />
    </div>
  )
}

export default DeleteButton
